import { useState, useRef } from "react";
import { ChevronDown, PencilLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const MAX_TYPE_LENGTH = 10;

// Common types shown as suggestions
const COMMON_TYPES = [
  {
    value: "feat",
    label: "feat",
    description: "New feature",
    colorClass: "text-[hsl(var(--success))]",
  },
  {
    value: "fix",
    label: "fix",
    description: "Bug fix",
    colorClass: "text-red-500",
  },
  {
    value: "docs",
    label: "docs",
    description: "Documentation",
    colorClass: "text-[hsl(var(--info))]",
  },
  {
    value: "chore",
    label: "chore",
    description: "Maintenance",
    colorClass: "text-[hsl(var(--muted-foreground))]",
  },
];

function getTypeColor(type: string): string {
  const found = COMMON_TYPES.find((t) => t.value === type);
  return found?.colorClass ?? "text-[hsl(var(--primary))]";
}

interface TypeDropdownProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function TypeDropdown({
  value = "feat",
  onChange,
  className,
}: TypeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (newValue: string) => {
    // Normalize: lowercase, alphanumeric and hyphens only, max 10 chars
    const normalized = newValue.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, MAX_TYPE_LENGTH);
    setInputValue(normalized);
    // Update form value in real-time for live preview
    if (normalized.length > 0) {
      onChange(normalized);
    }
  };

  const handleInputBlur = () => {
    // Apply value on blur if valid
    if (inputValue && inputValue.length > 0) {
      onChange(inputValue);
    } else {
      // Revert to previous value if empty
      setInputValue(value);
    }
  };

  const handleSelectType = (typeValue: string) => {
    setInputValue(typeValue);
    onChange(typeValue);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Tab") {
      handleInputBlur();
    }
  };

  return (
    <div className={cn("relative flex", className)}>
      <div className="relative flex-1">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder="feat"
          className={cn("pr-8 font-mono font-bold w-24", getTypeColor(inputValue))}
          maxLength={MAX_TYPE_LENGTH}
        />
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full w-8 rounded-l-none"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[240px]">
            {COMMON_TYPES.map((type) => (
              <DropdownMenuItem
                key={type.value}
                onClick={() => handleSelectType(type.value)}
                className={cn(
                  "font-mono",
                  value === type.value && "bg-[hsl(var(--accent))]"
                )}
              >
                <span className={cn("font-medium", type.colorClass)}>
                  {type.label}
                </span>
                <span className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">
                  {type.description}
                </span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                setInputValue("");
                setIsOpen(false);
                // Use multiple rAF to wait for Radix focus management to complete
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    inputRef.current?.focus();
                  });
                });
              }}
              className="font-mono text-[hsl(var(--primary))]"
            >
              <PencilLine className="mr-2 h-4 w-4" />
              Custom
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
